import { useState, useEffect } from 'react';
import { getDocument, queryDocuments } from '../firebase/firestore';

/**
 * Sanitize user data for public profile display.
 * Only exposes public fields, never sensitive data.
 */
function sanitizePublicProfile(user) {
  if (!user) return null;

  return {
    id: user.id,
    displayName: user.displayName || 'Anonymous',
    photoURL: user.photoURL || null,
    rating: user.rating || null,
    reviewCount: user.reviewCount || 0,
    createdAt: user.createdAt,
    // Stats (only show completed competitions)
    stats: {
      rafflesCreated: user.stats?.rafflesCreated || 0,
    },
    // Verification status (show badge but not details)
    phoneVerified: user.phoneVerified || false,
    // Location (area only, never full address)
    location: user.location || null,
    // Bio (if they've set one)
    bio: user.bio || null,
  };
}

/**
 * usePublicProfile - Fetch public profile data for a user
 *
 * Only returns publicly safe data (no email, phone, payment info).
 *
 * @param userId - The user's document ID
 */
export function usePublicProfile(userId) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let mounted = true;

    async function fetchProfile() {
      try {
        setLoading(true);
        setError(null);

        const userData = await getDocument('users', userId);

        if (mounted) {
          if (userData) {
            setProfile(sanitizePublicProfile(userData));
          } else {
            setError('User not found');
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching public profile:', err);
        if (mounted) {
          setError(err.message || 'Failed to load profile');
          setLoading(false);
        }
      }
    }

    fetchProfile();

    return () => {
      mounted = false;
    };
  }, [userId]);

  return { profile, loading, error };
}

/**
 * useSellerRaffles - Fetch a seller's competitions
 *
 * @param sellerId - The seller's user ID
 * @param options.status - Filter by status ('active', 'completed', 'all')
 * @param options.limit - Max number of results
 */
export function useSellerRaffles(sellerId, options = {}) {
  const { status = 'all', limit = 20 } = options;
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sellerId) {
      setLoading(false);
      return;
    }

    let mounted = true;

    async function fetchRaffles() {
      try {
        setLoading(true);
        setError(null);

        // Build query conditions
        const conditions = [['sellerId', '==', sellerId]];

        if (status === 'active') {
          conditions.push(['status', '==', 'active']);
        } else if (status === 'completed') {
          conditions.push(['status', '==', 'completed']);
        }
        // 'all' - no status filter, but exclude pending/cancelled for public view
        // Actually, let's only show active and completed to public
        if (status === 'all') {
          conditions.push(['status', 'in', ['active', 'completed']]);
        }

        const results = await queryDocuments('raffles', conditions, {
          orderBy: [['createdAt', 'desc']],
          limit,
        });

        if (mounted) {
          // Sanitize raffle data (remove pickupAddress)
          const sanitized = results.map(({ pickupAddress, ...safe }) => safe);
          setRaffles(sanitized);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching seller raffles:', err);
        if (mounted) {
          setError(err.message || 'Failed to load competitions');
          setLoading(false);
        }
      }
    }

    fetchRaffles();

    return () => {
      mounted = false;
    };
  }, [sellerId, status, limit]);

  return { raffles, loading, error };
}

/**
 * useSellerReviews - Fetch reviews for a seller
 *
 * @param sellerId - The seller's user ID (revieweeId)
 * @param options.limit - Max number of results
 * @param options.includeBreakdown - Calculate rating breakdown (5★: x, 4★: y, etc.)
 */
export function useSellerReviews(sellerId, options = {}) {
  const { limit = 10, includeBreakdown = false } = options;
  const [reviews, setReviews] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sellerId) {
      setLoading(false);
      return;
    }

    let mounted = true;

    async function fetchReviews() {
      try {
        setLoading(true);
        setError(null);

        // If we need breakdown, fetch all reviews (no limit) to calculate it
        const fetchLimit = includeBreakdown ? 1000 : limit;

        const results = await queryDocuments(
          'reviews',
          [['revieweeId', '==', sellerId]],
          {
            orderBy: [['createdAt', 'desc']],
            limit: fetchLimit,
          }
        );

        if (mounted) {
          // Calculate breakdown if requested
          if (includeBreakdown && results.length > 0) {
            const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
            results.forEach((review) => {
              const rating = Math.round(review.rating);
              if (rating >= 1 && rating <= 5) {
                counts[rating]++;
              }
            });
            setBreakdown(counts);
          }

          // Only return limited reviews for display
          setReviews(results.slice(0, limit));
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching seller reviews:', err);
        if (mounted) {
          setError(err.message || 'Failed to load reviews');
          setLoading(false);
        }
      }
    }

    fetchReviews();

    return () => {
      mounted = false;
    };
  }, [sellerId, limit, includeBreakdown]);

  return { reviews, breakdown, loading, error };
}

export default {
  usePublicProfile,
  useSellerRaffles,
  useSellerReviews,
};
